import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Data, ParamMap, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { InfoReportService } from 'src/app/services/info-report.service';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { EventsLeadsTargetsComponent } from './events-leads-targets/events-leads-targets.component';
import { InfoReportReviewComponent } from './info-report-review/info-report-review.component';
import { SummaryNotesComponent } from './summary-notes/summary-notes.component';
import { TrackingComponent } from './tracking/tracking.component';

@Component({
  selector: 'ness-info-report-form',
  templateUrl: './info-report-form.component.html'
})
export class InfoReportFormComponent implements OnDestroy, OnInit {
  @ViewChild('tracking', { static: false }) trackingComponent: TrackingComponent;
  @ViewChild('eventsLeads', { static: false }) eventsLeadsComponent: EventsLeadsTargetsComponent;
  @ViewChild('notesSummary', { static: false }) summaryNotesComponent: SummaryNotesComponent;
  @ViewChild('review', { static: false }) infoReportReviewComponent: InfoReportReviewComponent;

  private _activeTabIndex: number = 0;
  private _previousTabIndex: number = 0;
  private _informationReportData: ServerModels.ENESS_IntelligenceReports;
  private _returnUrl: string = null;
  private _unsubscribe$: Subject<void> = new Subject<void>();
  public _reviewReady: boolean = false;
  public _trackingSubmitted: boolean = false;
  public _eventsLeadsSubmitted: boolean = false;
  public _notesSummarySubmitted: boolean = false;
  public _updatedIR: ServerModels.ENESS_IntelligenceReports;
  public buttonTitle: string = 'Save report';
  public title: string = '';
  public clicked: boolean = false;

  get activeTabIndex(): number {
    return this._activeTabIndex;
  }

  set activeTabIndex(idx: number) {
    this._previousTabIndex = this._activeTabIndex !== this._previousTabIndex ? this._activeTabIndex : this._previousTabIndex;
    this._activeTabIndex = idx;
  }

  constructor(private _router: Router,
    private route: ActivatedRoute,
    private infoReportService: InfoReportService,
    private previousRouteService: PreviousRouteService
  ) { }

  ngOnInit() {
    this._returnUrl = this.previousRouteService.getReturnUrl();
    this.buttonTitle = this.route.snapshot.paramMap.get('id') ? 'Save report' : 'Create report';
    this.route.queryParamMap.pipe(takeUntil(this._unsubscribe$)).subscribe((params:ParamMap) => {
      this.activeTabIndex = +params.get('tabIndex') || 0;
    });

    this._initChildComponents();
  }

  ngOnDestroy(): void {
    this.previousRouteService.transferData = null;
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  private _initChildComponents() {
    this.route.paramMap.pipe(takeUntil(this._unsubscribe$)).subscribe((pm: ParamMap) => {
      const reportId: string = pm.get('id');
      if (reportId) {
        this.title = 'Edit Information Report';

        this.infoReportService.getInformationReportById(reportId).pipe(takeUntil(this._unsubscribe$)).subscribe({
          next: (ir: ServerModels.ENESS_IntelligenceReports) => {
            this._informationReportData = ir;
            this._updatedIR = JSON.parse(JSON.stringify(this._informationReportData));
            delete this._updatedIR.ENESS_REF_IntelligenceReportStatuses;
            delete this._updatedIR.ENESS_REF_IntelligenceReportTrackingIDs;
            delete this._updatedIR.ENESS_XREF_IRsATFCaseNumbers;
            this.infoReportService.updateInfoReportEvent(this._informationReportData);
          }
        });
      } else {
        this.title = 'Add Information Report';
        const transferData: Data = this.previousRouteService.getTransferData() || {};
        this._informationReportData = {
          IntelligenceReportID: null,
          IntelligenceTrackingNumber: null,
          Title: null,
          PriorityID: '',
          Summary: null,
          Notes: null,
          StatusID: '',
          ENESS_XREF_IRsPurposes: [],
          ENESS_XREF_IRsTypes: [],
          ENESS_XREF_IRsAssignedCGICPersonnel: [],
          ENESS_XREF_IRsATFCaseNumbers: [],
          ENESS_XREF_IRsCrimeGuns: transferData['ENESS_XREF_IRsCrimeGuns'] || [],
          ENESS_XREF_IRsDatesSent: [],
          ENESS_XREF_IRsLECaseNumbers: transferData['ENESS_XREF_IRsLECaseNumbers'] || [],
          ENESS_XREF_IRsPeopleTargets: [],
          ENESS_XREF_IRsAttachments: [],
          CreatedBy: null,
          CreatedOn: null,
          ModifiedBy: null,
          ModifiedOn: null
        } as ServerModels.ENESS_IntelligenceReports;

        this._updatedIR = JSON.parse(JSON.stringify(this._informationReportData));
        this.infoReportService.updateInfoReportEvent(this._informationReportData);
      }
    });
  }

  onTabChange(evt: {originalEvent: Event, index:number }) {
    switch (this._previousTabIndex) {
      case 0:
        this.trackingComponent.onSubmit();
        break;
      case 1:
        this.eventsLeadsComponent.onSubmit();
        break;
      case 2:
        this.summaryNotesComponent.onSubmit();
        break;
    }
    this.activeTabIndex = evt.index || 0;
    if (this.activeTabIndex == 3) {
      this._reviewReady = true;
    }
    else {
      this._reviewReady = false;
    }
    this._router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...this.route.snapshot.queryParams,
        tabIndex: this.activeTabIndex,
      }
    });
  }

  activateTab(tabIndex:number) {
    this.activeTabIndex = tabIndex;
    this._reviewReady = false;
  }

  gotoNextTab() {
    switch (this.activeTabIndex) {
      case 0:
        this.trackingComponent.onSubmit();
        break;
      case 1:
        this.eventsLeadsComponent.onSubmit();
        break;
      case 2:
        this.summaryNotesComponent.onSubmit();
        break;
    }
    this.activeTabIndex = this.activeTabIndex + 1;
    if (this.activeTabIndex == 3) {
      this._reviewReady = true;
    }
    else {
      this._reviewReady = false;
    }
  }

  gotoPrevTab() {
    switch (this.activeTabIndex) {
      case 0:
        this.trackingComponent.onSubmit();
        break;
      case 1:
        this.eventsLeadsComponent.onSubmit();
        break;
      case 2:
        this.summaryNotesComponent.onSubmit();
        break;
    }
    this.activeTabIndex = this.activeTabIndex - 1;
    this._reviewReady = false;
  }

  updateReportData(data: any) {
    switch (data.tab) {
      case 'trackingData':
        this._updatedIR.Title = data.formData.Title;
        this._updatedIR.CGICCode = data.formData.CGICCode;
        this._updatedIR.PriorityID = data.formData.PriorityID;
        this._updatedIR.StatusID = data.formData.StatusID;
        this._updatedIR.ENESS_XREF_IRsTypes = data.formData.ENESS_XREF_IRsTypes;
        this._updatedIR.ENESS_XREF_IRsPurposes = data.formData.ENESS_XREF_IRsPurposes;
        this._updatedIR.ENESS_XREF_IRsAssignedCGICPersonnel = data.formData.ENESS_XREF_IRsAssignedCGICPersonnel;
        this._trackingSubmitted = true;
        break;
      case 'eventsLeads':
        this._updatedIR.ENESS_XREF_IRsCrimeGuns = data.formData.ENESS_XREF_IRsCrimeGuns;
        this._updatedIR.ENESS_XREF_IRsLECaseNumbers = data.formData.ENESS_XREF_IRsLECaseNumbers;
        this._updatedIR.ENESS_XREF_IRsPeopleTargets = data.formData.ENESS_XREF_IRsPeopleTargets;
        this._eventsLeadsSubmitted = true;
        break;
      case 'notesSummary':
        this._updatedIR.Summary = data.formData.Summary;
        this._updatedIR.Notes = data.formData.Notes;
        this._updatedIR.ENESS_XREF_IRsAttachments = data.formData.ENESS_XREF_IRsAttachments;
        this._updatedIR.ENESS_XREF_IRsDatesSent = data.formData.ENESS_XREF_IRsDatesSent;
        this._notesSummarySubmitted = true;
        break;
    };


    // Force change detection on updatedIR
    this._updatedIR = Object.assign({}, this._updatedIR);
  }

  public saveInfoReport() {
    if (this.trackingComponent.trackingDataForm.valid && this._reviewReady) {
      this._updatedIR.ENESS_XREF_IRsTypes.forEach(w => {
        delete w.ENESS_REF_IntelligenceReportTypes;
      });
      this._updatedIR.ENESS_XREF_IRsPurposes.forEach(v => {
        delete v.ENESS_REF_IntelligenceReportPurposes;
      });
      this._updatedIR.ENESS_XREF_IRsLECaseNumbers.forEach(x => {
        delete x.ENESS_REF_State;
        delete x.ENESS_REF_Agency;
      });
      this._updatedIR.ENESS_XREF_IRsCrimeGuns.forEach(x => {
        delete x.ENESS_CrimeGunNumbers;
      });
      this._updatedIR.ENESS_XREF_IRsAttachments.forEach(y => {
        delete y.ENESS_IntelligenceReportAttachments;
      });

      this.infoReportService.addOrUpdateInformationReport(this._updatedIR).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (result:number) => {

          if (result > 0) {
            if (!!this._returnUrl) {
              this.previousRouteService.returnUrl = null;
              this._router.navigateByUrl(this._returnUrl);
            } else {
              this._router.navigateByUrl("/info-reports");
            }
          }
        }
      });
    }
  }
}
